import { ChangeDetectorRef, Component, Inject, Input, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AlarmGroup, alarmGroupNameMaxLimit, descriptionMaxLimit } from 'icreate/common/models/alarm-group';
import { ChannelCategory } from 'icreate/common/models/channel-category';
import { ListBubbleSelectorComponent } from '@components/list-bubble-selector/list-bubble-selector.component';
import { ChannelCategoryService } from '@service/channel/channel-category.service';
import { alarmGroupValidator } from 'icreate/common/validators/alarm-group/alarm-group-validator';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';

enum AlarmGroupInputName {
    NAME = 'name',
    DESCRIPTION = 'description',
}

@Component({
    selector: 'app-alarm-group-dialog',
    templateUrl: './alarm-group-dialog.component.html',
    styleUrls: ['./alarm-group-dialog.component.css'],
    standalone: false,
})
export class AlarmGroupDialogComponent {
    readonly NAME_INPUT = AlarmGroupInputName.NAME;
    readonly DESCRIPTION_INPUT = AlarmGroupInputName.DESCRIPTION;
    readonly NAME_MAX_LENGTH = alarmGroupNameMaxLimit;
    readonly DESCRIPTION_MAX_LENGTH = descriptionMaxLimit;

    @Input() vesselId: string;
    @ViewChild('categoriesSelector') categoriesSelector: ListBubbleSelectorComponent;

    validationErrors: ValidationErrorMap = {};
    editMode = false;
    isLoading = true;
    nameLength = 0;

    channelCategories: ChannelCategory[] = [];
    selectedCategories: ChannelCategory[] = [];

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            id: string;
            name: string;
            description: string;
            vesselId: string;
            groupNo: number;
            alarmGroupList: AlarmGroup[];
            categories?: ChannelCategory[];
            existingNames: string[];
        },
        private dialogRef: MatDialogRef<AlarmGroupDialogComponent>,
        private channelCategoryService: ChannelCategoryService,
        private cdr: ChangeDetectorRef
    ) {}

    async ngOnInit() {
        this.vesselId = this.data.vesselId;
        this.channelCategories = await this.channelCategoryService.getList(this.vesselId);

        this.selectedCategories = this.channelCategories.filter(cc =>
            this.data.categories?.some(sc => sc.category === cc.category)
        );
        if (this.data.name) {
            this.checkInputErrors();
        }
        this.isLoading = false;
    }

    onInputChange(event: { name: string; value: string }) {
        if (event.name === this.NAME_INPUT) {
            this.nameLength = event.value?.length ?? 0;
            this.data.name = event.value;
            this.checkInputErrors();
        }
        if (event.name === this.DESCRIPTION_INPUT) {
            this.data.description = event.value;
        }
        this.cdr.detectChanges();
    }

    onTextAreaChange(value) {
        this.data.description = value;
    }

    updateFilteredCategories(selectedCategories?: ChannelCategory[]) {
        if (selectedCategories) {
            this.selectedCategories = selectedCategories;
        }
    }

    get validatorFunction() {
        return alarmGroupValidator();
    }

    async createAlarmGroup() {
        try {
            const alarmGroup = {
                name: this.data.name,
                description: this.data.description,
                categories: this.selectedCategories,
            };

            this.dialogRef.close({
                alarmGroup,
                close: true,
            });
        } catch (error) {
            console.log(error);
        }
    }

    reset() {
        this.data.name = '';
        this.data.description = '';
        this.selectedCategories = [];
        this.categoriesSelector?.clearSelectedElements();
    }

    close() {
        this.dialogRef.close();
    }

    checkInputErrors() {
        const validationData = {
            name: this.data.name,
            description: this.data.description,
            categories: this.selectedCategories,
        };

        const errors = getValidationErrors(alarmGroupValidator(), validationData);
        const mappedErrors: { [key: string]: { message: string; params?: any } } = mapErrors(errors) || {};

        this.validationErrors = Object.keys(mappedErrors).length > 0 ? mappedErrors : null;

        if (!this.validationErrors.name) {
            const repeatedName = this.data.existingNames.some(name => name == this.data.name);
            if (repeatedName)
                this.validationErrors = {
                    name: { message: 'errors.validation.generic.conflic', params: { property: this.data.name } },
                };
        }
    }

    get hasErrors(): boolean {
        return !!(this.validationErrors?.name || this.validationErrors?.description);
    }
}
